import { createEnv } from '@t3-oss/env-core'
import { z } from 'zod'

/**
 * Every client var's schema, named rather than inlined so `coerceUnvalidated`
 * below and `__tests__/env.test.ts`'s guard can both walk them. A var added here
 * is coerced under test for free, and the guard picks it up without an edit —
 * for the class that can carry the bug at all, which is the schemas that
 * transform, coerce or default. An identity `z.string()` has nothing to get
 * wrong.
 *
 * @internal exported for that guard only — every consumer reads `env`.
 */
export const clientEnvSchemas = {
	// API
	VITE_AIR_API_URL: z.string().min(1),
	VITE_AI_API_URL: z.string().min(1),

	// Sentry
	VITE_SENTRY_DSN: z.string(),

	// USE_MOCK_DATA should be treated as a boolean, but .env files only allow strings
	VITE_USE_MOCK_DATA: z
		.string()
		.optional()
		.refine((string) => {
			// Only validate if the value is not undefined
			if (string) {
				return string === 'true' || string === 'false'
			}
			return true
		})
		.transform((string) => string === 'true'),

	// Demo build mode (ADR 0006 decision 2): offline, fully mocked build for
	// the public demo. Set only by `.env.demo` via `pnpm build:demo`.
	VITE_DEMO: z.coerce.boolean<boolean>().default(false),
	VITE_DISABLE_NOTIFICATIONS: z.coerce.boolean<boolean>().default(false),

	// PostHog
	VITE_POSTHOG_KEY: z.string().min(1),
	VITE_POSTHOG_HOST: z.string().min(1),

	// Clerk
	VITE_CLERK_PUBLISHABLE_KEY: z.string().min(1),
	VITE_CLERK_PRECONNECT_URL: z
		.string()
		.optional()
		.default('https://topical-pika-21.clerk.accounts.dev'),

	// Stage detection
	VITE_TARGET_STAGE: z
		.enum(['dev', 'prod', 'local', 'staging'])
		.default('local'),

	// Microsoft Frictionless Onboarding
	VITE_MICROSOFT_FRONTEND_REDIRECT_URL: z
		.url()
		.min(1)
		.default('http://localhost:3001/integrations/new/callback'),

	// END TO END TESTING
	VITE_E2E: z.coerce.boolean<boolean>().default(false),

	// Ephemeral API-capture dev tool (ADR 0007 / issue 01e). Dev-only; the
	// `@/lib/api` guard reads `import.meta.env.VITE_CAPTURE` directly (folded to
	// a literal by `vite.config.ts`) so the tool tree-shakes out of prod.
	VITE_CAPTURE: z.string().optional(),
}

const isTest = import.meta.env.MODE === 'test'

const parsedEnv = createEnv({
	/**
	 * The prefix that client-side variables must have. This is enforced both at
	 * a type-level and at runtime.
	 */
	clientPrefix: 'VITE_',

	client: clientEnvSchemas,

	/**
	 * What object holds the environment variables at runtime. This is usually
	 * `process.env` or `import.meta.env`.
	 */
	runtimeEnv: import.meta.env,

	/**
	 * By default, this library will feed the environment variables directly to
	 * the Zod validator.
	 *
	 * This means that if you have an empty string for a value that is supposed
	 * to be a number (e.g. `PORT=` in a ".env" file), Zod will incorrectly flag
	 * it as a type mismatch violation. Additionally, if you have an empty string
	 * for a value that is supposed to be a string with a default value (e.g.
	 * `DOMAIN=` in an ".env" file), the default value will never be applied.
	 *
	 * In order to solve these issues, we recommend that all new projects
	 * explicitly specify this option as true.
	 */
	emptyStringAsUndefined: true,

	// Vitest sets MODE='test' (issue 12: replaced VITE_TEST here — its build-time
	// Sentry-skip job moved to VITE_E2E). Under test the app boots with
	// placeholder env, so skip the strict client-var validation real builds enforce.
	// `VITE_CLERK_PUBLISHABLE_KEY` is the concrete reason it cannot simply be
	// turned on: it is supplied by the gitignored `.env.local`, so it is absent on
	// CI and a strict parse would fail every test file at import time.
	skipValidation: isTest,
})

/**
 * Re-apply each var's own schema to the values `skipValidation` waved through.
 *
 * env-core's escape hatch is `return runtimeEnv` — the raw object, unparsed
 * (`@t3-oss/env-core@0.13.11`, `dist/index.js:12-15`). So under Vitest every var
 * is the STRING Vite read out of `.env.test`, while its TypeScript type says
 * `boolean` — and `'false'` is truthy. The type lies in the one environment
 * whose job is to catch the lie: the dashboard draft store's module-scope
 * `if (isMockingEnabled)` fired in every unmocked test and wiped nine tests'
 * seeded `localStorage` drafts, against a flag that was set to `false`.
 *
 * Coercing is not validating. A key whose schema REJECTS keeps its raw value,
 * so the missing-var tolerance `skipValidation` buys is untouched — only keys
 * that parse are replaced by what they parsed to. This runs under test alone;
 * in a real build `createEnv` has already parsed every key.
 *
 * One consequence worth stating: this returns a COPY, so under test `env` is a
 * snapshot rather than the live `import.meta.env` env-core used to hand back. No
 * test stubs env vars today (`vi.stubEnv` appears nowhere), and one that did
 * would want to stub the module anyway — a coerced view cannot track a mutation
 * of its source.
 */
function coerceUnvalidated(runtimeEnv: typeof parsedEnv): typeof parsedEnv {
	const coerced: Record<string, unknown> = {
		...(runtimeEnv as Record<string, unknown>),
	}

	for (const [key, schema] of Object.entries(clientEnvSchemas)) {
		const result = schema.safeParse(coerced[key])
		if (result.success) coerced[key] = result.data
	}

	return coerced as typeof parsedEnv
}

export const env = isTest ? coerceUnvalidated(parsedEnv) : parsedEnv
